import React, { useRef, useEffect, useContext } from 'react';
import { NCContext } from '@wix/da-react-context/pkg/NCContext';
import { DuperbrowseItem, DuperbrowseItemId } from '../redux/types';
import LoadableDeviationPage from '../../DeviationPage/LoadableDeviationPage';
import { getStreamItemId } from '../redux/helpers';
import {
  DuperbrowseViewType,
  DuperbrowseViewContext,
} from '../DuperbrowseViewContext';
import { scrollTo } from '../../utils/browser';
import {
  DAMZ_PAGE_DUPERBROWSE_ITEMID,
  FEEDBACK_PAGE_DUPERBROWSE_ITEMID,
} from '../redux/constants';
import { useScrollRestore } from '../hooks/useScrollRestore';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { DamzContext } from '@wix/da-react-context/pkg/DamzContext';

export interface Props {
  isDuperbrowseActive: boolean;
  currentItem?: DuperbrowseItem;
  isLoading: boolean;
  justClosedAStream: boolean;
  viewType?: DuperbrowseViewType;
  children: React.ReactNode;
  exitDuperbrowse: () => void;
}

const DuperbrowsePageWrapper: React.FC<React.PropsWithChildren<Props>> = ({
  isDuperbrowseActive,
  currentItem,
  children,
  isLoading,
  justClosedAStream,
}) => {
  const { PageComponent } = useContext(NCContext);
  const { PageComponent: DamzPage } = useContext(DamzContext);
  // on navigation, scroll back top
  const lastItemId = useRef<DuperbrowseItemId>(undefined);
  const currentItemId = currentItem && getStreamItemId(currentItem);

  // We use duperbrowse to render the NC (feedback) page here, because having another
  // page wrapper will result in conflicts - one of the page wrappers higher
  // in the tree will take over the page earlier.
  //
  // For NC we have a special duperbrowse stream with just one item.
  //
  // Opening and closing the NC is achieved by dispatching toggleFeedbackPage,
  // which in turn delegates to openDuperbrowse/exitDuperbrowse actions.
  // Later on we also added damz which uses the same system as NC
  const isFeedbackPageOpen = currentItemId === FEEDBACK_PAGE_DUPERBROWSE_ITEMID;
  const isDamzPageOpen = currentItemId === DAMZ_PAGE_DUPERBROWSE_ITEMID;

  // Because we don't use DeviationLink to open the NC page, we have to
  // restore scroll by remembering the last window scroll position.
  useScrollRestore(isFeedbackPageOpen || isDamzPageOpen);

  useEffect(() => {
    // we do not want to scroll if we just returned from a nested stream
    // or if we don't have a current item (duper is inactive)
    // or if we are just re-rendering without a change to the current item
    if (
      !justClosedAStream &&
      currentItemId &&
      currentItemId !== lastItemId.current
    ) {
      scrollTo(window, 0, 0);
    }

    lastItemId.current = currentItemId;
  }, [currentItemId, justClosedAStream]);

  if (!isDuperbrowseActive) {
    return <>{children}</>;
  }

  if (isFeedbackPageOpen) {
    // PageComponent represents the FeedbackPageLoadable in da-feedback-shared.
    // We can't import it directly because of circular deps.
    return PageComponent ? (
      <PageComponent
        loadingIndicator={<LoadingIndicator view="llama" coverMode />}
      />
    ) : (
      <>{children}</>
    );
  }

  if (isDamzPageOpen) {
    // DamzPage represents the DamzPageLoadable in da-damz-shared.
    // We can't import it directly because of circular deps.
    return DamzPage ? (
      <DamzPage
        loadingIndicator={<LoadingIndicator view="llama" fillViewport />}
      />
    ) : (
      <>{children}</>
    );
  }

  return (
    <DuperbrowseViewContext.Provider value="fullscreen">
      <LoadableDeviationPage
        deviation={currentItem}
        isWholePageLoading={isLoading}
      />
    </DuperbrowseViewContext.Provider>
  );
};
DuperbrowsePageWrapper.displayName = 'DuperbrowsePageWrapper';

export default DuperbrowsePageWrapper;
